// US-AUTH-04 -- guardian resets a child's password.
//
// Reachable from the home screen's per-child section. Owns its own
// validation + submission state.

import {
  type AppError,
  type ChildPasswordResetResult,
  childPasswordReset,
} from '@chorepop/core/client';
import { type Component, Show, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

type Props = {
  sessionId: string;
  childAccountId: number;
  childDisplayName: string;
  onReset: (result: ChildPasswordResetResult) => void;
  onCancel: () => void;
};

export const ResetChildPasswordScreen: Component<Props> = (props) => {
  const [password, setPassword] = createSignal('');
  const [confirm, setConfirm] = createSignal('');
  const [submitting, setSubmitting] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setErrorMessage(null);

    if (password().length < 10) {
      setErrorMessage(`Password must be at least 10 characters (was ${password().length}).`);
      return;
    }
    if (password() !== confirm()) {
      setErrorMessage('The two passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await childPasswordReset({
        sessionId: props.sessionId,
        childAccountId: props.childAccountId,
        newPassword: password(),
      });
      if (!result.ok) {
        setErrorMessage(humanReadableError(result.error));
        return;
      }
      setPassword('');
      setConfirm('');
      props.onReset(result.value);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionScreen
      eyebrow={props.childDisplayName}
      title="Reset password"
      onBack={props.onCancel}
      footer={
        <button
          type="submit"
          form="reset-child-password-form"
          class="primary action-screen-primary"
          disabled={submitting()}
        >
          {submitting() ? 'Resetting…' : 'Reset password'}
        </button>
      }
    >
      <p class="action-screen-intro">
        Set a new password for {props.childDisplayName}. They'll use this to sign in next time.
        Their data and buckets are unchanged. Any login lockout is cleared.
      </p>

      <form id="reset-child-password-form" onSubmit={handleSubmit} novalidate class="action-form">
        <label class="field">
          <span class="field-label">New password</span>
          <input
            type="password"
            autocomplete="new-password"
            required
            autofocus
            disabled={submitting()}
            value={password()}
            onInput={(e) => {
              setPassword(e.currentTarget.value);
              setErrorMessage(null);
            }}
          />
          <span class="field-hint">At least 10 characters.</span>
        </label>

        <label class="field">
          <span class="field-label">Confirm new password</span>
          <input
            type="password"
            autocomplete="new-password"
            required
            disabled={submitting()}
            value={confirm()}
            onInput={(e) => {
              setConfirm(e.currentTarget.value);
              setErrorMessage(null);
            }}
          />
        </label>

        <Show when={errorMessage()}>
          {(msg) => (
            <div class="form-error" role="alert">
              {msg()}
            </div>
          )}
        </Show>
      </form>
    </ActionScreen>
  );
};

function humanReadableError(error: AppError): string {
  switch (error.kind) {
    case 'validation':
      return error.message;
    case 'unauthorized':
      return 'Your session has expired or you are not allowed to reset this account. Please sign in again.';
    case 'not_found':
      return 'That account no longer exists.';
    case 'network_required':
      return error.message;
    default:
      return `Something went wrong: ${error.message}`;
  }
}
